import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import './QuestThinking.css'

/**
 * Quest Thinking Eyes
 *
 * "Luna sees 'loading' not 'thinking.' - Diego
 *
 * Replaces mechanical bouncing dots with character-driven thinking animation.
 *
 * 4-phase cycle (1.4s total, matches existing spec):
 * - Phase 1 (0-350ms): Eyes up-left, "Hmm..."
 * - Phase 2 (350-700ms): Eyes up-right, "Or maybe..."
 * - Phase 3 (700-1050ms): Eyes squint, "Thinking harder..."
 * - Phase 4 (1050-1400ms): Eyes brighten, "Almost got it!"
 *
 * Why this works:
 * 1. Eyes UP = processing (universal human signal)
 * 2. Movement = alive (not frozen)
 * 3. Phase 4 = hope (Quest making progress)
 * 4. Loop is seamless
 *
 * @param {boolean} isThinking - Whether Quest is thinking
 */

const PHASE_DURATION = 350 // ms per phase
const TOTAL_CYCLE = 1400 // ms

const PHASES = [
  {
    id: 'hmm',
    emoji: '🤔',
    eyePosition: { x: -3, y: -3 }, // up-left
    tilt: -5,
    scale: 1,
    brightness: 1,
  },
  {
    id: 'maybe',
    emoji: '🧐',
    eyePosition: { x: 3, y: -3 }, // up-right
    tilt: 5,
    scale: 1,
    brightness: 1,
  },
  {
    id: 'thinking',
    emoji: '😑',
    eyePosition: { x: 0, y: 0 }, // center
    tilt: 0,
    scale: 0.95,
    brightness: 1,
  },
  {
    id: 'gotit',
    emoji: '💡',
    eyePosition: { x: 0, y: 0 }, // center
    tilt: 0,
    scale: 1.05,
    brightness: 1.2,
  },
]

export default function QuestThinking({ isThinking }) {
  const [phaseIndex, setPhaseIndex] = useState(0)

  useEffect(() => {
    if (!isThinking) {
      setPhaseIndex(0)
      return
    }

    // Cycle through phases
    const interval = setInterval(() => {
      setPhaseIndex(prev => (prev + 1) % PHASES.length)
    }, PHASE_DURATION)

    return () => clearInterval(interval)
  }, [isThinking])

  if (!isThinking) return null

  const phase = PHASES[phaseIndex]

  return (
    <div className="quest-thinking">
      <motion.div
        className="quest-thinking-avatar"
        animate={{
          rotate: phase.tilt,
          scale: phase.scale,
          filter: `brightness(${phase.brightness})`,
        }}
        transition={{
          duration: 0.2,
          ease: 'easeOut',
        }}
      >
        {/* Quest base */}
        <span className="quest-thinking-base">🗺️</span>

        {/* Eyes indicator that moves */}
        <motion.div
          className="quest-eyes"
          animate={{
            x: phase.eyePosition.x,
            y: phase.eyePosition.y,
          }}
          transition={{
            duration: 0.25,
            ease: 'easeInOut',
          }}
        >
          <span className="eye left">●</span>
          <span className="eye right">●</span>
        </motion.div>

        {/* Phase indicator */}
        <AnimatePresence mode="wait">
          <motion.span
            key={phase.id}
            className="quest-phase-indicator"
            initial={{ opacity: 0, scale: 0.8, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -5 }}
            transition={{ duration: 0.15 }}
          >
            {phase.emoji}
          </motion.span>
        </AnimatePresence>
      </motion.div>

      {/* Thinking dots (subtle, below the character) */}
      <div className="thinking-dots">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="thinking-dot"
            animate={{
              y: [0, -6, 0],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: i * 0.15,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>
    </div>
  )
}
